import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiShoppingCart,
  FiClipboard,
  FiUsers,
  FiDollarSign,
  FiCheckCircle,
  FiTrendingUp,
  FiCalendar,
  FiFileText,
  FiSettings,
} from "react-icons/fi";

const ModuleProcurement = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Procurement Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Procurement | RedERP</title>
        <meta
          name="description"
          content="Manage procurement requests, vendor relationships, quotations, and purchase orders in one place with RedERP’s Procurement module."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text Section */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Procurement</h1>
            <p className="mb-6 text-sm sm:text-base">
              Simplify your purchase lifecycle — from internal requisitions to purchase order approvals — while optimizing vendor selection and tracking.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Start Free Trial
            </a>
          </div>

          {/* Image */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/procurement1.png"
              alt="Procurement Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiClipboard size={24} />,
              title: "Purchase Requests",
              desc: "Create, review, and approve internal requisitions with tracking and accountability.",
            },
            {
              icon: <FiShoppingCart size={24} />,
              title: "Purchase Orders",
              desc: "Generate POs directly from approved requests or low-stock alerts with full traceability.",
            },
            {
              icon: <FiUsers size={24} />,
              title: "Vendor Management",
              desc: "Manage supplier contacts, contracts, payment terms, and performance ratings.",
            },
            {
              icon: <FiDollarSign size={24} />,
              title: "Quotation Comparison",
              desc: "Request, collect, and compare vendor quotations — choose based on cost, lead time, or score.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Approval Workflows",
              desc: "Define multi-level approval rules and notify managers automatically for validation.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Procurement KPIs",
              desc: "Track average purchase cost, lead times, supplier fill rate, and more.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Delivery Scheduling",
              desc: "Coordinate delivery dates and expected arrivals for multi-supplier projects.",
            },
            {
              icon: <FiFileText size={24} />,
              title: "Documents & Compliance",
              desc: "Attach terms, contracts, and invoices directly to procurement transactions.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Purchase Policies",
              desc: "Set limits, approvals, and conditions based on department or purchase type.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Procure Smarter with RedERP</h3>
          <p className="mb-6">Try our powerful procurement tools — no credit card required.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try Procurement Module
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleProcurement;
